import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components/macro";

import HrefButton from "../common/HrefButton";
import Button from "../common/Button";
import Loader from "../../components/Loader/ModalLoaderIcon";
import AudioControls from "./AudioControls";
import audioThumb from "../../assets/images/audio-poster.png";
import { colors } from "../themes/base";

const CardWrapper = styled.div`
  background: ${(props) => props.background};
  width: ${(props) => props.width};
  overflow: hidden;
  font-family: ${(props) => props.fontFamily};
  border: ${(props) => props.cardBorder};
  border-radius: 3px;
  color: ${(props) => props.cardFontColor};

  @media only screen and (max-width: 500px) {
    width: 80%;
    min-width: 80%;
  }

  .video-container {
    width: 100%;
    height: 21.5rem;
    position: relative;
    background: white;
    display: flex;
    justify-content: center;
    border-top-left-radius: 3px;
    border-top-right-radius: 3px;

    img {
      position: absolute;
      left: 50%;
      transform: translateX(-50%);
      top: 20%;
      width: 25%;
      height: auto;
      object-fit: cover;
      border-top-right-radius: 3px;
      border-top-left-radius: 3px;
      z-index: 10;
    }

    audio {
      position: absolute;
      left: 3px;
      bottom: 0;
      width: 99%;
      height: 4rem;
    }
  }
`;

const VideoDetails = styled.div`
  box-sizing: border-box;
  padding: 1.6rem;
  width: 100%;

  .title-container {
    font-size: 1.8rem;
    width: calc(100% - 10px);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .timestamp {
    padding-top: 0.6rem;
    opacity: 0.5;
    font-size: 1.2rem;
  }
`;

const ActionButtonsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  padding: 0 1.5rem 1.6rem 1.5rem;
  @media only screen and (max-width: 500px) {
    button {
      margin-bottom: 1rem;
    }
  }
  .buttonWrapper {
    display: flex;
    flex: 1;
    button:not(:last-child) {
      margin-right: 0.5rem;
    }
  }
  .deleteBtn {
    ${(props) => (props.deleteStatus ? "pointer-events: none;" : "")};
  }
`;

const buttonProps = {
  fontSize: "1.1rem",
  boxShadow: "none",
  borderRadius: "3rem",
  padding: "0.7rem 1rem",
};

const ActionButtons = (props) => {
  if (props.redacted) {
    return (
      <ActionButtonsWrapper {...props}>
        <div className="buttonWrapper">
          {props.deleteStatus ? (
            <HrefButton
              disabled
              border="1px solid #e4e7ee"
              color={colors.button}
              background="#fff"
              {...buttonProps}
            >
              Download
            </HrefButton>
          ) : (
            <HrefButton
              border="1px solid #e4e7ee"
              color={colors.button}
              background="#fff"
              href={props.href}
              {...buttonProps}
            >
              Download
            </HrefButton>
          )}
        </div>
        <Button
          className="deleteBtn"
          deleteStatus={props.deleteStatus}
          color="red"
          background="#F9FAFD"
          border="none"
          onClick={props.handleDelete}
          {...buttonProps}
        >
          {props.deleteStatus ? <Loader /> : "Delete"}
        </Button>
      </ActionButtonsWrapper>
    );
  }
  return (
    <ActionButtonsWrapper {...props}>
      <div className="buttonWrapper">
        {props.deleteStatus ? (
          <Button
            disabled
            border="1px solid #e4e7ee"
            background="#fff"
            color={colors.button}
            onClick={props.handleRedact}
            {...buttonProps}
          >
            Redact
          </Button>
        ) : (
          <Button
            border="1px solid #e4e7ee"
            background="#fff"
            color={colors.button}
            onClick={props.handleRedact}
            {...buttonProps}
          >
            Redact
          </Button>
        )}

        {props.deleteStatus ? (
          <HrefButton
            disabled
            border="1px solid #e4e7ee"
            background="#fff"
            color={colors.button}
            {...buttonProps}
          >
            Download
          </HrefButton>
        ) : (
          <HrefButton
            border="1px solid #e4e7ee"
            background="#fff"
            color={colors.button}
            {...buttonProps}
            href={props.href}
          >
            Download
          </HrefButton>
        )}
      </div>
      <Button
        className="deleteBtn"
        deleteStatus={props.deleteStatus}
        color="red"
        border="none"
        background="#F9FAFD"
        onClick={props.handleDelete}
        {...buttonProps}
      >
        {props.deleteStatus ? <Loader /> : "Delete"}
      </Button>
    </ActionButtonsWrapper>
  );
};

class AudioPlayerCard extends React.Component {
  constructor(props) {
    super(props);
    this.audioRef = React.createRef();
    this.state = {
      isPlaying: false,
      isMuted: false,
      duration: 0,
      currentTime: 0,
      currentVolume: 1,
      hasLoaded: false,
    };
  }

  play = () => {
    const { isPlaying } = this.state;
    if (isPlaying) {
      this.audioRef.current.pause();
    } else {
      this.audioRef.current.play();
    }
  };

  setCurrentTime = (time) => {
    this.setState({ currentTime: time });
    this.audioRef.current.currentTime = time;
  };

  muteAudio = () => {
    const { isMuted } = this.state;
    const muted = !isMuted;
    if (isMuted) {
      this.audioRef.current.muted = muted;
    } else {
      this.audioRef.current.muted = muted;
    }
    this.setState({ isMuted: muted });
  };

  setVolume = (volume) => {
    this.setState({ currentVolume: volume });
    this.audioRef.current.volume = volume;
  };

  render() {
    const { props } = this;
    return (
      <CardWrapper {...props}>
        <div className="video-container">
          <img src={audioThumb} alt={props.videoTitle} />
          <audio
            ref={this.audioRef}
            controlsList="nodownload"
            preload={"none"}
            src={props.videoURL}
            onPlay={() => this.setState({ isPlaying: true })}
            onPause={() => this.setState({ isPlaying: false })}
            onLoadedMetadata={() =>
              this.setState({
                duration: this.audioRef.current.duration,
                hasLoaded: true,
              })
            }
            onTimeUpdate={() => {
              if (this.state.hasLoaded) {
                const video = this.audioRef.current;
                let currentTime = video.currentTime;
                if (currentTime > this.state.duration) {
                  currentTime = this.state.duration;
                }

                this.setState({ currentTime });
              }
            }}
            onVolumeChange={() => {
              const isMuted = this.audioRef.current.muted;
              if (isMuted) {
                this.setState({ currentVolume: 0 });
              } else {
                this.setState({ currentVolume: this.audioRef.current.volume });
              }
            }}
          ></audio>
          <AudioControls
            play={this.play}
            mute={this.muteAudio}
            setCurrentTime={this.setCurrentTime}
            setVolume={this.setVolume}
            isPlaying={this.state.isPlaying}
            isMuted={this.state.isMuted}
            duration={this.state.duration}
            currentTime={this.state.currentTime}
            currentVolume={this.state.currentVolume}
            hasLoaded={this.state.hasLoaded}
          />
        </div>

        <VideoDetails>
          <div className="title-container">
            <span className="title">{props.videoTitle}</span>
          </div>

          {props.timestamp ? (
            <div className="timestamp">{props.timestamp}</div>
          ) : null}
        </VideoDetails>

        <ActionButtons {...props} />
      </CardWrapper>
    );
  }
}

export default AudioPlayerCard;

AudioPlayerCard.propTypes = {
  background: PropTypes.string,
  width: PropTypes.string,
  fontFamily: PropTypes.string,
  cardBorder: PropTypes.string,
  cardBoxShadow: PropTypes.string,
  cardFontColor: PropTypes.string,
  videoURL: PropTypes.string,
  videoTitle: PropTypes.string,
  poster: PropTypes.string,
  starred: PropTypes.bool,
  redacted: PropTypes.bool,
  timestamp: PropTypes.string,
  handleRedact: PropTypes.func,
  handleSave: PropTypes.func,
  handleDelete: PropTypes.func,
};

AudioPlayerCard.defaultProps = {
  width: "auto",
  background: "#FFFFFF",
  poster:
    "https://icon-library.net/images/audio-file-icon/audio-file-icon-21.jpg",
  fontFamily: '"Muli", sans-serif',
  cardBorder: "1px solid rgba(20, 41, 69, 0.14)",
  cardFontColor: "#142945",
  starred: false,
  redacted: false,
};
